import { useState, useEffect } from 'react';
import { formatLocalDate, formatTime } from '../../utils/dateFormat';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Plus, Pencil, Trash2, Users, Clock, MapPin, Loader2, Lock, Globe, School, Building2, Video, Sparkles } from 'lucide-react';

export default function AdminWorkshopsPage({ embedded }) {
  const { api } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [formData, setFormData] = useState({
    course: '',
    location: '',
    instructor: '',
    term: 'Spring 2025',
    room: '',
    start_date: '',
    end_date: '',
    meeting_days: [],
    start_time: '09:00',
    end_time: '12:00',
    lesson_duration: 180,
    capacity: 30,
    price: 49.99,
    active: true,
    enrollment_restriction: 'open',
    delivery_type: 'in_person',
    meeting_link: '',
    meeting_password: '',
    section_type: 'workshop',
    total_sessions: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, coursesRes, locationsRes, instructorsRes] = await Promise.all([
        api.get('/sections/?active_only=false&section_type=workshop&page_size=5000'),
        api.get('/courses/?page_size=5000'),
        api.get('/locations/'),
        api.get('/users/?role=instructor')
      ]);
      setWorkshops(sectionsRes.data?.results || sectionsRes.data || []);
      setCourses(coursesRes.data?.results || coursesRes.data || []);
      setLocations(locationsRes.data?.results || locationsRes.data || []);
      setInstructors(instructorsRes.data?.results || instructorsRes.data || []);
    } catch (error) {
      toast.error('Failed to load workshops');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        instructor: formData.instructor || null,
        location: formData.delivery_type === 'online' ? null : (formData.location || null),
        meeting_days: formData.meeting_days.length ? formData.meeting_days : [getDayFromDate(formData.start_date)],
        section_type: 'workshop',
        total_sessions: formData.total_sessions || 1,
        end_date: formData.total_sessions === 1 ? formData.start_date : formData.end_date,
        end_time: calculateEndTime(formData.start_time, formData.lesson_duration),
      };
      delete payload.lesson_duration;
      
      if (editingWorkshop) {
        await api.put(`/sections/${editingWorkshop.id}/`, payload);
        toast.success('Workshop updated');
      } else {
        await api.post('/sections/', payload);
        toast.success('Workshop created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save workshop');
    }
  };

  const getDayFromDate = (dateStr) => {
    if (!dateStr) return 'Saturday';
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workshop?')) return;
    try {
      await api.delete(`/sections/${id}/`);
      toast.success('Workshop deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete workshop');
    }
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const resetForm = () => {
    setFormData({
      course: '',
      location: '',
      instructor: '',
      term: 'Spring 2025',
      room: '',
      start_date: '',
      end_date: '',
      meeting_days: [],
      start_time: '09:00',
      end_time: '12:00',
      lesson_duration: 180,
      capacity: 30,
      price: 49.99,
      active: true,
      enrollment_restriction: 'open',
      delivery_type: 'in_person',
      meeting_link: '',
      meeting_password: '',
      section_type: 'workshop',
      total_sessions: 1
    });
    setEditingWorkshop(null);
  };

  const openEditDialog = (workshop) => {
    setEditingWorkshop(workshop);
    const inferredDuration = (() => {
      if (workshop.start_time && workshop.end_time) {
        const [sh, sm] = workshop.start_time.split(':').map(Number);
        const [eh, em] = workshop.end_time.split(':').map(Number);
        return (eh * 60 + em) - (sh * 60 + sm);
      }
      return 180;
    })();
    setFormData({
      course: workshop.course,
      location: workshop.location || '',
      instructor: workshop.instructor || '',
      term: workshop.term,
      room: workshop.room || '',
      start_date: workshop.start_date,
      end_date: workshop.end_date,
      meeting_days: Array.isArray(workshop.meeting_days) ? workshop.meeting_days : (workshop.meeting_days ? [workshop.meeting_days] : []),
      start_time: workshop.start_time,
      end_time: workshop.end_time,
      lesson_duration: inferredDuration,
      capacity: workshop.capacity,
      price: workshop.price,
      active: workshop.active,
      enrollment_restriction: workshop.enrollment_restriction || 'open',
      delivery_type: workshop.delivery_type || 'in_person',
      meeting_link: workshop.meeting_link || '',
      meeting_password: workshop.meeting_password || '',
      section_type: 'workshop',
      total_sessions: 1
    });
    setDialogOpen(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      meeting_days: prev.meeting_days.includes(day)
        ? prev.meeting_days.filter(d => d !== day)
        : [...prev.meeting_days, day]
    }));
  };

  const getSelectedLocation = () => {
    return locations.find(l => l.id === formData.location);
  };

  const getRestrictionBadge = (workshop) => {
    if (workshop.enrollment_restriction === 'school_only') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          <School className="h-3 w-3 mr-1" />
          {workshop.restricted_to_school_name || 'School Only'}
        </Badge>
      );
    }
    if (workshop.enrollment_restriction === 'district_only') {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700">
          <Building2 className="h-3 w-3 mr-1" />
          {workshop.restricted_to_district_name || 'District Only'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700">
        <Globe className="h-3 w-3 mr-1" />
        Open
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedLocation = getSelectedLocation();

  return (
    <div className="space-y-6" data-testid="admin-workshops-page">
      <div className="flex justify-between items-center">
        {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-500" />
            Workshops
          </h1>
          <p className="text-muted-foreground">Manage one-time workshops and special events</p>
        </div>
        )}
        <div className={embedded ? 'ml-auto' : ''}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-workshop-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Workshop
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingWorkshop ? 'Edit Workshop' : 'Add New Workshop'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Workshop Type Toggle */}
              <div className="space-y-2">
                <Label className="font-semibold">Workshop Format</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.total_sessions === 1 ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setFormData({...formData, total_sessions: 1, end_date: formData.start_date, meeting_days: []})}
                    data-testid="single-session-btn"
                  >
                    Single Session
                  </Button>
                  <Button
                    type="button"
                    variant={formData.total_sessions === 2 ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setFormData({...formData, total_sessions: 2, end_date: ''})}
                    data-testid="two-session-btn"
                  >
                    Two Sessions
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {formData.total_sessions === 1 ? 'A single-day workshop event' : 'A workshop spanning two separate days'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course *</Label>
                  <Select value={formData.course} onValueChange={(v) => setFormData({...formData, course: v})} required>
                    <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      {courses.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Instructor</Label>
                  <Select value={formData.instructor || 'unassigned'} onValueChange={(v) => setFormData({...formData, instructor: v === 'unassigned' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select instructor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {instructors.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.first_name} {i.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date & Time */}
              <div className="p-4 bg-amber-50/50 dark:bg-amber-950/10 rounded-lg border border-amber-200/50 space-y-3">
                <Label className="font-semibold flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <Calendar className="h-4 w-4" />
                  {formData.total_sessions === 1 ? 'Date & Time' : 'Dates & Time'}
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{formData.total_sessions === 1 ? 'Workshop Date *' : 'Session 1 Date *'}</Label>
                    <Input 
                      type="date" 
                      value={formData.start_date} 
                      onChange={(e) => setFormData({
                        ...formData, 
                        start_date: e.target.value,
                        end_date: formData.total_sessions === 1 ? e.target.value : formData.end_date
                      })} 
                      required 
                    />
                  </div>
                  {formData.total_sessions === 2 && (
                    <div>
                      <Label>Session 2 Date *</Label>
                      <Input 
                        type="date" 
                        value={formData.end_date} 
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})} 
                        required 
                        min={formData.start_date}
                      />
                    </div>
                  )}
                  <div>
                    <Label>Start Time *</Label>
                    <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value, end_time: calculateEndTime(e.target.value, formData.lesson_duration)})} />
                  </div>
                  <div>
                    <Label>Duration</Label>
                    <Select value={String(formData.lesson_duration)} onValueChange={(v) => {
                      const dur = parseInt(v);
                      setFormData({...formData, lesson_duration: dur, end_time: calculateEndTime(formData.start_time, dur)});
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">1 Hour</SelectItem>
                        <SelectItem value="90">1.5 Hours</SelectItem>
                        <SelectItem value="120">2 Hours</SelectItem>
                        <SelectItem value="150">2.5 Hours</SelectItem>
                        <SelectItem value="180">3 Hours</SelectItem>
                        <SelectItem value="240">4 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ends at {formatTime(formData.end_time || calculateEndTime(formData.start_time, formData.lesson_duration))}
                  {formData.total_sessions === 2 && formData.meeting_days.length > 0 && ` — meets on ${formData.meeting_days.join(', ')}`}
                </p>
              </div>

              {/* Meeting days for 2-session workshops */}
              {formData.total_sessions === 2 && (
                <div>
                  <Label>Meeting Days (select which days the workshop meets)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <Badge
                        key={day}
                        variant={formData.meeting_days.includes(day) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleDay(day)}
                      >
                        {day.slice(0, 3)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Location & Logistics */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Delivery Type *</Label>
                  <Select 
                    value={formData.delivery_type} 
                    onValueChange={(v) => setFormData({...formData, delivery_type: v})}
                  >
                    <SelectTrigger data-testid="delivery-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select 
                    value={formData.location || 'none'} 
                    onValueChange={(v) => setFormData({...formData, location: v === 'none' ? '' : v})}
                    disabled={formData.delivery_type === 'online'}
                  >
                    <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Location</SelectItem>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input type="number" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} />
                </div>
                <div>
                  <Label>Room</Label>
                  <Input 
                    value={formData.room} 
                    onChange={(e) => setFormData({...formData, room: e.target.value})} 
                    placeholder="Room 101"
                    disabled={formData.delivery_type === 'online'}
                  />
                </div>
                <div>
                  <Label>Term *</Label>
                  <Select value={formData.term} onValueChange={(v) => setFormData({...formData, term: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spring 2025">Spring 2025</SelectItem>
                      <SelectItem value="Summer 2025">Summer 2025</SelectItem>
                      <SelectItem value="Fall 2025">Fall 2025</SelectItem>
                      <SelectItem value="Winter 2026">Winter 2026</SelectItem>
                      <SelectItem value="Spring 2026">Spring 2026</SelectItem>
                      <SelectItem value="Summer 2026">Summer 2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.price} 
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} 
                  />
                </div>
              </div>
              
              {/* Online Workshop Settings */}
              {(formData.delivery_type === 'online' || formData.delivery_type === 'hybrid') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg space-y-4 border border-blue-200 dark:border-blue-800">
                  <Label className="font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Video className="h-4 w-4" />
                    Online Workshop Settings
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Meeting Link (Zoom/Google Meet)</Label>
                      <Input 
                        type="url"
                        value={formData.meeting_link} 
                        onChange={(e) => setFormData({...formData, meeting_link: e.target.value})} 
                        placeholder="https://zoom.us/j/123456789"
                        data-testid="meeting-link-input"
                      />
                    </div>
                    <div>
                      <Label>Meeting Password (Optional)</Label>
                      <Input 
                        value={formData.meeting_password} 
                        onChange={(e) => setFormData({...formData, meeting_password: e.target.value})} 
                        placeholder="Meeting password"
                        data-testid="meeting-password-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Enrollment Restriction */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Enrollment Restriction
                </Label>
                <Select 
                  value={formData.enrollment_restriction} 
                  onValueChange={(v) => setFormData({...formData, enrollment_restriction: v})}
                >
                  <SelectTrigger data-testid="restriction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open to All Students</SelectItem>
                    <SelectItem value="school_only">School Students Only</SelectItem>
                    <SelectItem value="district_only">District Students Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-workshop-btn">{editingWorkshop ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold">{workshops.length}</p>
            <p className="text-sm text-muted-foreground">Total Workshops</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Video className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
            <p className="text-2xl font-bold">{workshops.filter(w => w.delivery_type === 'online' || w.delivery_type === 'hybrid').length}</p>
            <p className="text-sm text-muted-foreground">Online/Hybrid</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{workshops.filter(w => new Date(w.start_date) >= new Date()).length}</p>
            <p className="text-sm text-muted-foreground">Upcoming</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{workshops.reduce((sum, w) => sum + w.enrolled_count, 0)}</p>
            <p className="text-sm text-muted-foreground">Total Enrolled</p>
          </CardContent>
        </Card>
      </div>

      <Card className="card-dashboard">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workshop</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Restriction</TableHead>
                <TableHead>Enrollment</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workshops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No workshops found. Add your first workshop above.
                  </TableCell>
                </TableRow>
              ) : (
                workshops.map((workshop) => (
                  <TableRow key={workshop.id} data-testid={`workshop-row-${workshop.id}`}>
                    <TableCell className="font-medium">{workshop.course_title}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {formatLocalDate(workshop.start_date)}
                        {workshop.start_date !== workshop.end_date && (
                          <span className="text-muted-foreground"> - {formatLocalDate(workshop.end_date, { month: 'short', day: 'numeric' })}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatTime(workshop.start_time)} - {formatTime(workshop.end_time)}
                    </TableCell>
                    <TableCell>
                      {workshop.delivery_type === 'online' ? (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Video className="h-4 w-4" />
                          <span>Online</span>
                        </div>
                      ) : workshop.delivery_type === 'hybrid' ? (
                        <div>
                          <div className="flex items-center gap-1 text-purple-600">
                            <Video className="h-4 w-4" />
                            <span>Hybrid</span>
                          </div>
                          <div className="text-sm text-muted-foreground">{workshop.location_name}</div>
                        </div>
                      ) : (
                        <div>
                          <div>{workshop.location_name}</div>
                          {workshop.room && <div className="text-sm text-muted-foreground">Room: {workshop.room}</div>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getRestrictionBadge(workshop)}</TableCell>
                    <TableCell>
                      <span className={workshop.enrolled_count >= workshop.capacity ? 'text-red-600 font-bold' : ''}>
                        {workshop.enrolled_count}/{workshop.capacity}
                      </span>
                    </TableCell>
                    <TableCell>${(Number(workshop.price) || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={workshop.active ? 'default' : 'secondary'}>
                        {workshop.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(workshop)} data-testid={`edit-workshop-${workshop.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(workshop.id)} data-testid={`delete-workshop-${workshop.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
